import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder } from '@angular/forms';
import { EMPTY, Observable, concat, defer, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ItemWriteService } from './item-write.service';
import { IEditItemRequest, IEditItemResult, IItemProblemDetails, ItemResponse } from './item.models';
import { buildEditRequest, IEditLookups, seedEditForm } from './rules/item-write-edit.rules';
import { fanOut } from './rules/item-write-fanout';
import { createItemWriteForm } from './rules/item-write-form.factory';
import { formErrorNames } from './rules/item-write-messages';
import { applyWritability } from './rules/item-write-writability.rules';

/** At most this many PUTs in flight (HUBX-8477). */
export const MAX_SAVES_IN_FLIGHT = 5;

/** One edited row, as the listing holds it (HUBX-8479).
 *
 *  `edited` is the row's full state after the user's changes — under a full-state PUT the
 *  edited state of a row IS a complete ItemResponse. The status choice is carried as
 *  `effectiveStatus`, the value the collapsed control holds, exactly as in the modal; the
 *  axes are derived from it at build time. The two tokens and `isArchived` are read from
 *  `edited` too: they are the row's own, and move only when that row is rebased.
 *
 *  `baseline` is the row as the page loaded it (or as its last save or 412 left it): what
 *  the status rules compare with, the source of the echo-only members, and whose
 *  `writableFields` decide what this caller may write on this item. */
export interface IBatchRow {
  baseline: ItemResponse;
  edited: ItemResponse;
}

/** What happened to one row. Bound to its id by construction — there is no line number
 *  to map back, which is what legacy's single N-item POST needed.
 *
 *  - `saved`: the PUT returned 200. `result` carries the ROTATED token, so the caller can
 *    drop the row from its edited set and adopt the new version.
 *  - `failed`: the PUT was sent and refused. `problem` is the ProblemDetails (routed by its
 *    stable `code`; a 412 carries `currentState`). The caller keeps the row, with its
 *    tokens unrotated, so it can be corrected and retried.
 *  - `invalid`: the shared validators refused it, so it was NEVER sent. `errors` names the
 *    failing controls and the form-level rules (e.g. `moqExceedsMxq`). */
export type RowSaveOutcome =
  | { id: string; status: 'saved'; result: IEditItemResult }
  | { id: string; status: 'failed'; httpStatus: number; problem: IItemProblemDetails | null }
  | { id: string; status: 'invalid'; errors: string[] };

/** A refused request as that row's outcome: every failure becomes a value, so one row's
 *  failure never stops the others. Shared by the PUT and the affected-offers check
 *  (HUBX-8511), so both report a failure the same way and the row shows the same message. */
export function failedOutcome(id: string, err: unknown): RowSaveOutcome {
  return {
    id,
    status: 'failed',
    httpStatus: err instanceof HttpErrorResponse ? err.status : 0,
    // A ProblemDetails is a JSON object. A network failure hands over a ProgressEvent
    // instead, which is not one and must not be routed as if it had a `code`.
    problem: err instanceof HttpErrorResponse && err.error && typeof err.error === 'object'
      && !(err.error instanceof Event)
      ? err.error as IItemProblemDetails
      : null
  };
}

/** A row that passed the pre-flight: the exact body its PUT will carry. The offers check
 *  sends it as is (POST /items/{id}/affected-offers, HUBX-8511) and the fan-out PUTs it. */
export interface IPreparedRow {
  id: string;
  itemId: string;
  request: IEditItemRequest;
}

/** What the fan-out needs besides the rows: the lookups the forms seed through, and the
 *  ROLE gate the form factory and the pricing rule read. Per-ITEM permission is not here —
 *  it travels on each row's `writableFields`. */
export interface IBatchSaveContext {
  lookups: IEditLookups;
  pricing: boolean;
}

/** The pre-flight for one row: build its form exactly as a surface would, narrow it to what
 *  the server granted, run the shared validators, and — only if they pass — produce the
 *  body the modal would send for the same state.
 *
 *  Locked controls are disabled by applyWritability and therefore not validated, which is
 *  the modal's behaviour too: a Seller past review cannot be blocked by an attribute it is
 *  not allowed to touch.
 *
 *  An archived edit is inert, as its row and the modal hold it (HUBX-8466): the whole form is
 *  disabled, so nothing on it is validated and the archive intent itself can be saved. Without
 *  this, an item whose stored data the validators refuse showed a live Save on its archived
 *  row - a form it cannot edit - and was refused here. The body comes from getRawValue(), so
 *  disabling changes nothing in it. */
export function prepareRowSave(
  fb: FormBuilder,
  row: IBatchRow,
  ctx: IBatchSaveContext
): { kind: 'ready'; request: IEditItemRequest } | { kind: 'invalid'; errors: string[] } {
  const fg = createItemWriteForm(fb, ctx.pricing);
  seedEditForm(fg, row.edited, ctx.lookups);
  const writableFields = row.baseline.writableFields ?? [];
  applyWritability(fg, writableFields);
  if (row.edited.isArchived) {
    fg.disable({ emitEvent: false });
  }
  fg.updateValueAndValidity();

  if (fg.invalid) {
    return { kind: 'invalid', errors: formErrorNames(fg) };
  }

  return {
    kind: 'ready',
    request: buildEditRequest(fg.getRawValue(), {
      origin: row.baseline,
      lookups: ctx.lookups,
      writableFields,
      pricing: ctx.pricing,
      isArchived: row.edited.isArchived,
      expectedRowVersion: row.edited.rowVersion,
      expectedItemRowVersion: row.edited.itemRowVersion
    })
  };
}

/**
 * Multi-row save: N single-item PUTs in place of legacy's one
 * `POST VendorItem/internalVendorItem` carrying N items. v2 has no batch endpoint, and
 * each row is its own aggregate with its own tokens.
 *
 * - **Pre-flight, per row.** A row the shared validators refuse is reported `invalid` and
 *   never sent — the same gate the modal applies on Save.
 * - **At most 5 in flight, never two rows on the same master.** Rows sharing an `itemId`
 *   contend on the `Items` rowversion; running them together returns a 412 the user did not
 *   cause. They run one after the other instead.
 * - **No stop at first failure.** Every row runs. Cutting short would leave an arbitrary
 *   prefix applied with nothing to say which.
 *
 * Two steps, prepare() and send(), because the offers check runs between them (see
 * prepare). save() chains the two for a caller with no check. Rendering the outcomes is
 * HUBX-8478.
 */
@Injectable({
  providedIn: 'root'
})
export class ItemBatchSaveService {
  constructor(private itemWriteService: ItemWriteService, private fb: FormBuilder) { }

  /** Pre-flight + send in one call, for a caller with no check in between. */
  save(rows: IBatchRow[], ctx: IBatchSaveContext): Observable<RowSaveOutcome> {
    // Deferred: nothing is validated or sent until someone subscribes, and a second
    // subscription is a second, independent save rather than a replay.
    return defer(() => {
      const { ready, invalid } = this.prepare(rows, ctx);
      return concat(from(invalid), this.send(ready));
    });
  }

  /** Step 1 of 2: the per-row pre-flight. Nothing is sent.
   *
   *  Split from send() because the offers check sits BETWEEN the two: it must ask about
   *  exactly the bodies that will go out (HUBX-8511), and the rows whose offer the user
   *  keeps leave the batch before anything is sent. The listing therefore calls
   *  prepare -> check -> send(ready minus kept). */
  prepare(rows: IBatchRow[], ctx: IBatchSaveContext): { ready: IPreparedRow[]; invalid: RowSaveOutcome[] } {
    const ready: IPreparedRow[] = [];
    const invalid: RowSaveOutcome[] = [];
    for (const row of rows) {
      const prepared = prepareRowSave(this.fb, row, ctx);
      if (prepared.kind === 'ready') {
        ready.push({ id: row.edited.id, itemId: row.edited.itemId, request: prepared.request });
      } else {
        invalid.push({ id: row.edited.id, status: 'invalid', errors: prepared.errors });
      }
    }
    return { ready, invalid };
  }

  /** Step 2 of 2: the fan-out. One outcome per row as it settles, then completes. Cold:
   *  nothing is sent until subscribed. */
  send(ready: IPreparedRow[]): Observable<RowSaveOutcome> {
    return ready.length
      ? fanOut(ready, r => r.itemId, r => this.put(r.id, r.request), MAX_SAVES_IN_FLIGHT)
        .pipe(map(({ result }) => result))
      : EMPTY;
  }

  /** One PUT, with every failure turned into a value so the fan-out keeps going. */
  private put(id: string, request: IEditItemRequest): Observable<RowSaveOutcome> {
    return this.itemWriteService.editItem(id, request).pipe(
      map((result): RowSaveOutcome => ({ id, status: 'saved', result })),
      catchError((err: unknown) => of(failedOutcome(id, err)))
    );
  }
}
